/**
 * React hooks for GTD Next Action operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GTDClient } from '../../../client';
import { 
  CreateNextActionRequest,
  AssignNextActionRequest,
  NextActionQueryFilters,
  ListQueryParams 
} from '../../../contracts';
import { useGTDClient } from '../provider';

// Query keys
const ACTIONS_QUERY_KEY = ['gtd', 'actions'] as const;
const CONTEXTS_QUERY_KEY = ['gtd', 'contexts'] as const;

export function useNextActions(filters?: NextActionQueryFilters & ListQueryParams) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...ACTIONS_QUERY_KEY, filters],
    queryFn: () => client.getNextActions(filters),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useNextAction(id: string | null) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...ACTIONS_QUERY_KEY, id],
    queryFn: () => client.getNextAction(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useActionsByProject(projectId: string | null) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...ACTIONS_QUERY_KEY, 'project', projectId],
    queryFn: () => client.getActionsByProject(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
}

export function useActionsByContext(context: string | null) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...ACTIONS_QUERY_KEY, 'context', context],
    queryFn: () => client.getActionsByContext(context!),
    enabled: !!context,
    staleTime: 30 * 1000,
  });
}

export function useContexts() {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: CONTEXTS_QUERY_KEY,
    queryFn: () => client.getContexts(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateNextAction() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: CreateNextActionRequest) => 
      client.createNextAction(request),
    onSuccess: (data) => {
      // Invalidate actions lists
      queryClient.invalidateQueries({ queryKey: ACTIONS_QUERY_KEY });
      
      // If it has a project, invalidate that project's actions
      if (data.projectId) {
        queryClient.invalidateQueries({ 
          queryKey: [...ACTIONS_QUERY_KEY, 'project', data.projectId] 
        });
      }
      
      // If it has a context, invalidate that context's actions
      if (data.context) {
        queryClient.invalidateQueries({ 
          queryKey: [...ACTIONS_QUERY_KEY, 'context', data.context] 
        });
      }
    },
  });
}

export function useAssignNextAction() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: AssignNextActionRequest }) =>
      client.assignNextAction(id, request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData([...ACTIONS_QUERY_KEY, variables.id], data);
      queryClient.invalidateQueries({ 
        queryKey: ACTIONS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useUnassignNextAction() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.unassignNextAction(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData([...ACTIONS_QUERY_KEY, id], data);
      queryClient.invalidateQueries({ 
        queryKey: ACTIONS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useCompleteNextAction() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.completeNextAction(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData([...ACTIONS_QUERY_KEY, id], data);
      queryClient.invalidateQueries({ 
        queryKey: ACTIONS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useDeleteNextAction() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.deleteNextAction(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [...ACTIONS_QUERY_KEY, id] });
      queryClient.invalidateQueries({ 
        queryKey: ACTIONS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

// Weekly Review
export function useWeeklyReview(reviewedBy: string) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: ['gtd', 'weekly-review', reviewedBy],
    queryFn: () => client.getWeeklyReview(reviewedBy),
    staleTime: 60 * 1000, // 1 minute
  });
}